<template>
  <div class="container-fluid">
    <div class="row">
        <div class="col-12">
            <form id="bidding_form" @submit.prevent="save">
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="d-flex justify-content-between" v-if="bidding">
                            <div class="d-grid gap-2 d-flex">
                                <button 
                                    class="btn btn-xs btn-outline-primary text-xs font-weight-normal m-0" 
                                    @click="edit = true"
                                    v-if="!edit && bidding.state_value === 'draft'">
                                    <i class="fa fa-pencil-square-o" aria-hidden="true"></i> Edit Bid
                                </button>
                                <template v-if="edit">
                                    <button type="submit" :class="'btn btn-xs btn-primary text-xs font-weight-normal m-0 ' + (loading ? 'disabled' : '')">
                                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="loading"></span>
                                        <i class="fa fa-save" aria-hidden="true" v-if="!loading"></i> 
                                        Save
                                    </button>
                                    <button @click="discardBid" class="btn btn-xs btn-outline-secondary text-xs font-weight-normal m-0">
                                        <i class="fa fa-times" aria-hidden="true"></i> Discard
                                    </button>
                                </template>
                            </div>
                            <template v-if="bidding.state_value === 'draft'">
                                <argon-badge color="secondary" class="text-xxs align-self-center">{{ bidding.state }}</argon-badge>
                            </template>
                            <template v-else-if="bidding.state_value === 'review'">
                                <argon-badge color="info" class="text-xxs align-self-center">{{ bidding.state }}</argon-badge>
                            </template>
                            <template v-else-if="bidding.state_value === 'accepted'">
                                <argon-badge color="success" class="text-xxs align-self-center">{{ bidding.state }}</argon-badge>
                            </template>
                            <template v-else-if="bidding.state_value === 'win'">
                                <argon-badge color="primary" class="text-xxs align-self-center">{{ bidding.state }}</argon-badge>
                            </template>
                            <template v-else>
                                <argon-badge color="danger" class="text-xxs align-self-center">{{ bidding.state }}</argon-badge>
                            </template>
                        </div>
                        <hr/>
                    </div>
                    <div class="card-body min-vh-50 pt-0" v-if="bidding">
                        <h5>{{ bidding.bidding_name }}</h5>
                        <div class="row">
                            <div class="col-sm-6">
                                <table class="text-sm" width="100%">
                                    <tbody>
                                        <tr>
                                            <td class="text-secondary align-top" width="30%">Vendor</td>
                                            <td class="fw-bold align-top">{{ bidding.partner_name }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-secondary align-top">Estimated Delivery Date</td>
                                            <td class="fw-bold align-top" v-if="!edit">{{ bidding.estimated_date }}</td>
                                            <td class="font-weight-bold align-top" v-else>
                                            <Datepicker
                                                v-model="estimated_date" 
                                                format="dd MMMM yyyy"
                                                :min-date="bidding.sh_delivery_date ? bidding.sh_delivery_date : false"
                                                required
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="col-sm-6">
                                <table class="text-sm" width="100%">
                                    <tbody>
                                      <tr>
                                        <td class="text-secondary align-top" width="30%">Tender Number</td>
                                        <td class="fw-bold align-top">{{ bidding.tender_name }}</td>
                                      </tr>
                                      <tr>
                                          <td class="text-secondary align-top" width="30%">Supplier Note</td>
                                          <td class="fw-bold align-top">
                                              <input 
                                                  v-model="bidding.note"
                                                  class="form-control form-control-sm mb-0" 
                                                  type="text"
                                                  v-if="edit"/>
                                              <template v-else>
                                                  {{ bidding.note }}
                                              </template>
                                          </td>
                                      </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="table-responsive p-0 mt-3">
                            <table class="table align-items-center mb-0">
                                <thead>
                                    <tr class="bg-light">
                                        <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">#</th>
                                        <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Product</th>
                                        <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Quantity</th>
                                        <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">UoM</th>
                                        <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Unit Price (Exclude VAT)</th>
                                        <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="line,i in bidding_lines" :key="line.bidding_line_id">
                                        <td class="text-sm mb-0 ps-2">{{ i+1 }}</td>
                                        <td>
                                            <p class="text-sm mb-0">{{ line.product_name }}</p>
                                        </td>
                                        <td>
                                            <p class="text-sm text-end mb-0">{{ line.qty }}</p>
                                        </td>
                                        <td>
                                            <p class="text-sm mb-0">{{ line.uom_name }}</p>
                                        </td>
                                        <td>
                                            <input 
                                            :ref="'bidPrice' + line.bidding_line_id" 
                                            class="form-control form-control-sm mb-0" 
                                            type="number" 
                                            :value="line.price_unit_value"
                                            @change="onchangePrice($event,i)"
                                            required=""
                                            step="any"
                                            v-if="edit"/>
                                            <p class="text-sm text-end mb-0" v-else>{{ line.price_unit }}</p>
                                        </td>
                                        <td>
                                            <p class="text-sm text-end mb-0 px-2">{{ line.price_subtotal }}</p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="row mt-3">
                            <div class="col-md offset-md-8 px-4">
                                <table class="text-sm fw-bold" width="100%">
                                    <tbody>
                                        <template v-if="bidding.vendor_tax_identity_pkp">
                                          <tr>
                                              <td class="border-top">Amount Tax</td>
                                              <td class="border-top fw-bold text-end">{{ bidding.amount_tax }}</td>
                                          </tr>
                                        </template>
                                        <tr>
                                            <td class="border-top">Amount Total</td>
                                            <td class="border-top fw-bold text-end">{{ bidding.amount_total }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="row mt-3 text-sm" v-if="bidding.remark">
                            <div class="col-md-6">
                                <p class="mb-0 text-sm">Remark</p>
                                <p class="mb-0 fw-bold text-sm">{{ bidding.remark }}</p>
                            </div>
                        </div>
                        <div class="row mt-3" v-if="bidding_history.length > 0">
                          <div class="col-md">
                            <p class="text-secondary text-sm">RFP Price History</p>
                            <div class="table-responsive p-0">
                              <table class="table align-items-center mb-0">
                                  <thead>
                                      <tr class="bg-light">
                                          <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">#</th>
                                          <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Product</th>
                                          <th class="text-uppercase text-xs fw-bolder opacity-7 text-end ps-2">Quantity</th>
                                          <th class="text-uppercase text-xs fw-bolder opacity-7 text-end ps-2">Price Before</th>
                                          <th class="text-uppercase text-xs fw-bolder opacity-7 text-end ps-2">Price After</th>
                                          <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Update Date</th>
                                          <th></th>
                                          <th></th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="line,i in bidding_history" :key="i">
                                      <td class="text-sm mb-0 ps-2">{{ i+1 }}</td>
                                      <td>
                                        <p class="text-sm mb-0">{{ line.product_name }}</p>
                                      </td>
                                      <td>
                                        <p class="text-sm text-end mb-0">{{ line.quantity }}</p>
                                      </td>
                                      <td>
                                        <p class="text-sm text-end mb-0 text-danger">{{ line.price_before }}</p>
                                      </td>
                                      <td>
                                        <p class="text-sm text-end mb-0 text-success">{{ line.price_after }}</p>
                                      </td>
                                      <td>
                                        <p class="text-sm mb-0">{{ line.date_updated }}</p>
                                      </td>
                                    </tr>
                                  </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <div class="row mt-3">
                            <div class="col-md">
                              <p class="text-secondary text-sm">Documents</p>
                              <div class="table-responsive p-0">
                                <table class="table align-items-center mb-0">
                                    <thead>
                                        <tr class="bg-light">
                                            <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">#</th>
                                            <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Document Name</th>
                                            <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Submission Deadline</th>
                                            <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Required</th>
                                            <th></th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="line,i in bidding_documents" :key="line.agreement_bid_document_id">
                                        <td class="text-sm mb-0 ps-2">{{ i+1 }}</td>
                                        <td>
                                          <p class="text-sm mb-0">{{ line.name }}</p>
                                        </td>
                                        <td>
                                          <p class="text-sm mb-0">{{ line.deadline }}</p>
                                        </td>
                                        <td>
                                          <argon-badge color="danger" class="text-xxs align-self-center" v-if="line.required">Required</argon-badge>
                                        </td>
                                        <template v-if="line.url_data || line.file">
                                          <td>
                                            <a :href="line.url_data">{{ line.filename }}</a>
                                          </td>
                                          <td>
                                            <button v-if="edit" type="button" class="btn btn-link text-danger btn-xs p-0" @click="clearDoc(i)">
                                              <i class="fa text-sm fa-times" aria-hidden="true"></i> Clear
                                            </button>
                                          </td>
                                        </template>
                                        <template v-else>
                                          <td>
                                            <input v-if="edit" type="file" class="form-control" :accept="allowed_extensions" :required="line.required" :ref="'documentUpload' + line.agreement_bid_document_id" @change="getDocumentTender($event,i)"/>
                                          </td>
                                        </template>
                                      </tr>
                                    </tbody>
                                </table>
                              </div>
                              <p class="text-danger text-end text-sm mt-3 fst-italic fw-bold">Note : Maximum file size that can be uploaded is 20 MB (all tender supporting documents)</p>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
  </div>
</template>

<script>
import ArgonBadge from '../components/ArgonBadge.vue'
const endpoint = require("../router/endpoint");

export default {
  name: "bidding-detail",
  data() {
    return {
      bidding: null,
      loading: false,
      bidding_lines: [],
      bidding_payments: [],
      bidding_documents: [],
      bidding_history: [],
      estimated_date: null,
      edit: false,
      bid_line_ids: [],
      supplier_note: null,
      uploaded: false,
      allowed_extensions: localStorage.getItem('allowed_extensions')
    };
  },
  components: {
    ArgonBadge
  },
  methods: {
    getBiddingDetail() {
      this.loading = true
      this.axios
        .post(
          endpoint.BIDDING_LIST,
          {
            partner_id: parseInt(localStorage.getItem("partner_id")),
            bid_id: parseInt(this.$route.params.id.split('_')[0]),
            limit: 1,
            offset: 0
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        )
        .then((response) => {
          if (response.data.msg_status === "0002"){
            this.$notify({
                type: 'warn',
                title: 'Expired',
                text: 'Session expired, please sign in again.',
            });
            this.$router.replace('/signin')
            return false
          } else if (response.data.response_code === "0000") {
            this.bidding = response.data.data[0];
            this.loading = false;
            if (this.bidding.estimated_date) {
                this.estimated_date = new Date(this.bidding.estimated_date)
            }
            this.getBiddingDocumentList(this.bidding.tender_id.split('_')[0])
          } else {
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });
            this.loading = false;
          }
        })
        .catch((e) => {
          this.$notify({
              type: 'error',
              title: 'Something went wrong!',
              text: e.response.data.message || e,
          });
          this.loading = false;
          this.$router.replace('/request-for-proposal')
        });
    },
    getBiddingHistory() {
      this.loading = true
      this.axios
        .post(
          endpoint.BIDDING_HISTORY,
          {
            partner_id: parseInt(localStorage.getItem("partner_id")),
            bid_id: parseInt(this.$route.params.id.split('_')[0]),
            limit: 10000,
            offset: 0
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        )
        .then((response) => {
          if (response.data.msg_status === "0002"){
            this.$notify({
                type: 'warn',
                title: 'Expired',
                text: 'Session expired, please sign in again.',
            });
            this.$router.replace('/signin')
            return false
          } else if (response.data.response_code === "0000") {
            this.bidding_history = response.data.data;
            this.loading = false;
          } else {
            this.loading = false;
          }
        })
        .catch((e) => {
          this.$notify({
              type: 'error',
              title: 'Something went wrong!',
              text: e.response.data.message || e,
          });
          this.loading = false;
          this.$router.replace('/request-for-proposal')
        });
    },
    onchangePrice(event,index) {
      this.bidding_lines[index].price_unit_value = event.target.value
    },
    getBiddingLines() {
      this.loading = true
      this.axios
        .post(
        endpoint.BIDDING_LINES,
        {
          partner_id: parseInt(localStorage.getItem("partner_id")),
          bid_id: parseInt(this.$route.params.id.split('_')[0]),
          limit: 1000,
          offset: 0
        },
        {
            headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
            }
        }
        )
        .then((response) => {
        if (response.data.msg_status === "0002"){
          this.$notify({
              type: 'warn',
              title: 'Expired',
              text: 'Session expired, please sign in again.',
          });
          this.$router.replace('/signin')
          return false
        } else if (response.data.response_code === "0000") {
            response.data.data.forEach(line => {
                line.price_unit_value = Number(line.price_unit.replace(/[^0-9.-]+/g,""))
            });
            this.bidding_lines = response.data.data;
        } else {
            if (response.data.response_code !== "4000"){
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });}
            this.loading = false;
        }
        })
        .catch((e) => {
        this.loading = false;
        this.$notify({
            type: 'error',
            title: 'Something went wrong!',
            text: e.response.data.message || e,
            });
        });
    },
    getBiddingDocumentList(tender_id) {
      this.loading = true
      this.axios
        .post(
          endpoint.BIDDING_DOCUMENT,
          {
            partner_id: parseInt(localStorage.getItem('partner_id')),
            agreement_id: parseInt(tender_id),
            limit: 100000,
            offset: 0,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        )
        .then((response) => {
          if (response.data.msg_status === "0002"){
            this.$notify({
                type: 'warn',
                title: 'Expired',
                text: 'Session expired, please sign in again.',
            });
            this.$router.replace('/signin')
            return false
          } else if (response.data.response_code === "0000") {
            this.bidding_documents = response.data.data;
            this.bidding_documents.forEach(line => {
              line.model_id = line.model_id || 0
            });
            this.loading = false;
          } else {
            if (response.data.response_code !== "4000"){
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });}
            this.loading = false;
          }
        })
        .catch((e) => {
          this.$notify({
              type: 'error',
              title: 'Something went wrong!',
              text: e.response.data.message || e,
          });
          this.loading = false;
        });
    },
    async waitForUploadDocument() {
      try {
        this.loading = true;
        var req_empty = 0;

        this.bidding_documents.forEach(doc => {
          if (doc.required) {
            if (!doc.file && !doc.url_data) {
              req_empty = req_empty + 1;
            }
          }
        });

        if (req_empty > 0) {
          this.$notify({
            type: 'warn',
            title: 'Warning!',
            text: 'Required documents must be uploaded!',
          });
          this.loading = false;
          return false;
        }

        var self = this;

        for (const [i, doc] of this.bidding_documents.entries()) {
          if (doc.file) {
            const params = {
              files_to_upload: doc.file,
              file_to_upload_filename: doc.filename,
              model_name: 'bh.document.vendor',
              model_id: parseInt(doc.document_vendor_id.split('_')[0]),
              partner_id: parseInt(localStorage.getItem('partner_id')),
              document_id: parseInt(doc.document_id.split('_')[0]),
              required: doc.required,
            };
            try {
                const response = await self.axios.post(
                endpoint.UPLOAD_DOCUMENT,
                params,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                  }
                }
              );

              if (response.data.msg_status === '0002') {
                this.$notify({
                  type: 'warn',
                  title: 'Expired',
                  text: 'Session expired, please sign in again.',
                });
                this.$router.replace('/signin');
                return false;
              } else if (response.data.response_code === '0000') {
                self.bidding_documents[i].url_data = response.data.data.url_data;
              } else {
                if (response.data.response_code !== '4000') {
                  self.$notify({
                    type: 'warn',
                    title: 'Warning',
                    text: response.data.message,
                  });
                }
                self.loading = false;
                return false;
              }
            } catch (e) {
              if (e.response && e.response.status === 413) {
                // Handle error 413 Payload Too Large
                self.$notify({
                  type: 'error',
                  title: 'Payload Too Large',
                  text: 'The file size is too large. Please upload a smaller file.',
                });
              } else {
                // Handle other errors
                self.$notify({
                  type: 'error',
                  title: 'Something went wrong!',
                  text: e.response.data.message || e,
                });
              }

              self.loading = false;
              return false;
            }
          }
        }

        return true;
      } catch (error) {
        console.error('Error in waitForUploadDocument:', error);
        this.loading = false
        return false;
      }
    },
    discardBid () {
      this.edit = false
      this.getBiddingDocumentList(this.bidding.tender_id.split('_')[0])
    },
    clearDoc(index) {
      this.bidding_documents[index].url_data = null
      this.bidding_documents[index].file = null
      this.bidding_documents[index].filename = null
      this.bidding_documents[index].file_size = null
    },
    getDocumentTender(event, index) {
      const fileInput = this.$refs['documentUpload' + this.bidding_documents[index].agreement_bid_document_id];

      const file = event.target.files[0];

      // Validasi ukuran file maksimum (misalnya, 20 MB)
      let limit_file = localStorage.getItem('limit_file') || 20;
      const maxSizeInBytes = limit_file * 1024 * 1024; // Konversi MB ke bytes
      if (file.size > maxSizeInBytes) {
        // Tampilkan pesan atau ambil tindakan yang sesuai
        alert(`File size exceeds the maximum allowed size (${limit_file} MB). Please choose a smaller file.`);

        // Kosongkan input file
        this.bidding_documents[index].file = null;

        // Set nama file ke string kosong
        this.bidding_documents[index].filename = '';

        // Kosongkan nilai input file di DOM
        fileInput.value = null;

        return;
      }
      this.bidding_documents[index].file_size = file.size
      const reader = new FileReader();
      reader.onload = e => { 
        this.bidding_documents[index].file = e.target.result.split(',')[1];
      };

      reader.readAsDataURL(file);
      this.bidding_documents[index].filename = file.name;
    },
    confirm() {
      this.bid_line_ids = []
      const month = this.estimated_date.getMonth() >= 9 ? (this.estimated_date.getMonth() + 1) : '0' + (this.estimated_date.getMonth() + 1)
      const day = this.estimated_date.getDate() < 10 ? ('0' + this.estimated_date.getDate()) : this.estimated_date.getDate()
      this.loading = true
      this.bidding_lines.forEach(line => {
        this.bid_line_ids.push({
          'id': parseInt(line.bidding_line_id.split('_')[0]),
          'qty': line.qty,
          'price_unit': parseFloat(this.$refs['bidPrice'+line.bidding_line_id].value),
        })
      });
      var bid_list = []
      this.bidding_documents.forEach(bid => {
        bid_list.push({
          agreement_bid_document_id: parseInt(bid.agreement_bid_document_id.split('_')[0]),
          document_id: parseInt(bid.document_id.split('_')[0]),
          filename: bid.filename,
          url_data: bid.url_data
        })
      });
      const params = {
          bid_id: parseInt(this.$route.params.id.split('_')[0]),
          bid_line_ids: this.bid_line_ids,
          estimated_date: this.estimated_date.getFullYear() + '-' + month + '-' + day,
          partner_id: parseInt(localStorage.getItem('partner_id')),
          currency_id: parseInt(this.bidding.currency_id.split('_')[0]), 
          note: this.bidding.note,
          bid_document_ids: bid_list,
          is_pkp: localStorage.getItem('is_pkp'),
          limit: 0,
          offset: 0,
        }
      this.axios
      .post(
        endpoint.BIDDING_UPDATE,
        params,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      )
      .then((response) => {
        if (response.data.msg_status === "0002"){
          this.$notify({
              type: 'warn',
              title: 'Expired',
              text: 'Session expired, please sign in again.',
          });
          this.$router.replace('/signin')
          return false
        } else if (response.data.response_code === "0000") {
          // success
          this.loading = false
          this.edit = false
          this.getBiddingDetail()
          this.getBiddingLines()
          this.getBiddingHistory()
          this.$notify({
            type: 'success',
            title: 'Voila!',
            text: 'Successfully Update Bidding',
          });
        } else {
          // sys error
          this.loading = false;
          this.$notify({
            type: 'error',
            title: 'Oops!',
            text: response.data.message,
          });
        }
      })
      .catch((e) => {
        this.loading = false;
        this.$notify({
            type: 'error',
            title: 'Error!',
            text: e.response.data.message || e,
          });
      });
    },
    save() {
      const totalFileSize = this.bidding_documents.reduce((sum, document) => {
        // Periksa apakah document.file_size terdefinisi dan memiliki nilai numerik
        if (document.file_size !== undefined && typeof document.file_size === 'number' && !isNaN(document.file_size)) {
          return sum + document.file_size;
        } else {
          // Jika tidak valid, kembalikan nilai sum tanpa penambahan
          return sum;
        }
      }, 0);
      let limit_file = localStorage.getItem('limit_file') || 20; // default 20 jika tidak ada nilai dari localStorage
      const maxSizeInBytes = limit_file * 1024 * 1024; // Konversi MB ke bytes
      if (totalFileSize > maxSizeInBytes) {
        alert(`File size exceeds the maximum allowed size (${limit_file} MB). Please choose a smaller file.`);
      } else {
        this.waitForUploadDocument().then((res) => {
          if (res) {
            this.confirm();
          }
        })
      }
    },
  },
  mounted() {
    this.getBiddingDetail();
    this.getBiddingLines();
    this.getBiddingHistory();
  }
};
</script>

<style scoped>
  .btn {
    margin-bottom: 0 !important; 
    letter-spacing: 0 !important;
  }
</style>